import Box from '@mui/material/Box';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import { CuratedPackageAction, PackageFields, type PackageFormState } from '@openx/types/package.ts';
import { UserRole } from '@openx/types/user.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePackageContext } from '../../../utils/context/PackageContext.tsx';
import { packageLabels } from '../../PackageForm/constants.ts';
import { PackageEstimatedRangesSection } from './PackageEstimatedRangesSection.tsx';

type PackageDetailsProps = {
  pckg: PackageFormState;
};

export function PackageDetails({ pckg }: PackageDetailsProps) {
  const { t } = useTranslation();
  const { useCurrentInstanceState } = useTargetingContext();
  const { usePackagePermissions } = usePackageContext();
  const { isBefInstance } = useCurrentInstanceState();
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed, accessUser } = useIsAllowed();
  const { hasCuratedPermission } = usePackagePermissions();

  const displayLockedInternalCheckbox =
    isBefInstance &&
    (isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE)) ||
      accessUser?.roles.includes(UserRole.NETWORK_OA_OPENX_BUYER));

  const displayCuratedCheckbox = useMemo(
    () => hasCuratedPermission({ action: CuratedPackageAction.UPDATE, pckg: { is_curated: true } }),
    [hasCuratedPermission]
  );

  return (
    <FormSection divider title={t('Package Details')}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          {displayLockedInternalCheckbox && (
            <FieldDetails
              checkbox
              title={packageLabels(t)[PackageFields.LOCKED_INTERNAL]}
              value={pckg.locked_internal}
              dataTest="locked-internal-field"
            />
          )}

          {displayCuratedCheckbox && (
            <FieldDetails
              checkbox
              title={packageLabels(t)[PackageFields.IS_CURATED]}
              value={pckg.is_curated}
              dataTest="curated-package-field"
            />
          )}

          <FieldDetails title={t('Description')} value={pckg.notes} />
        </Box>

        {pckg.is_curated && pckg.estimates && <PackageEstimatedRangesSection estimates={pckg.estimates} />}
      </Box>
    </FormSection>
  );
}
